import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Flex from 'components/common/Flex';
import { useAppContext } from 'providers/AppProvider';
import { Card, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as echarts from 'echarts/core';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from 'components/common/ReactEchart';
import { totalAlgoRequest } from 'hooks/CallApi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const AlgorithmCompare = () => {
  const { getThemeColor } = useAppContext();
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // 로딩 상태 추가

  const encryptionChartRef = useRef(null);
  const decryptionChartRef = useRef(null);

  const {
    config: { dashboardDate, dashboardDateValue, Chart_startdate, Chart_enddate, isDemo },
    setConfig
  } = useAppContext();

  const [dateRange, setDateRange] = useState({
    start: Chart_startdate,
    end: Chart_enddate
  });

  useEffect(() => {
    // Chart_startdate 또는 Chart_enddate가 변경될 때 상태 업데이트
    setDateRange({
      start: Chart_startdate,
      end: Chart_enddate
    });
  }, [Chart_startdate, Chart_enddate]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true); // 데이터 로드 시작 시 로딩 상태 활성화 

      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await totalAlgoRequest({
          period: dashboardDateValue, // or 'week', 'month', 'custom'
          start: dateRange.start,
          end: dateRange.end,
          timeZone: clientTimeZone,
          demo: isDemo
        });
        if (cancelled) return;

        // API 데이터를 상태로 설정
        setChartData(response.data.algoInfoList);
      } catch (error) {
        if (cancelled) return;
        console.error('전체 알고리즘 요청 데이터 가져오기 에러:', error);
      } finally {
        if (!cancelled) setIsLoading(false); // 데이터 로드 완료 시 로딩 상태 비활성화
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dashboardDateValue, dateRange, isDemo]);
  // 암호화 및 복호화에 대한 성공 및 실패 처리량 계산
  // const firstValue = Math.round(TotalValue * firstRatio);
  // const secondValue = Math.round(TotalValue * secondRatio);
  // const thirdValue = Math.round(TotalValue * thirdRatio);

  const getOption = () => {
    if (!chartData) {
      return {};
    }

    const colorMapping = {
      AES: '#3B82F6',
      ARIA: '#10B981',
      SEED: '#8B5CF6',
      RSA: '#F59E0B'
    };

    const formattedData = chartData.map((item) => ({
      value: item.requestCnt,
      name: item.algoName,
      requestPer: item.requestPer, // requestPer 추가

      itemStyle: {
        color: colorMapping[item.algoName] || '#CCCCCC' // 기본 색상은 회색으로 설정
      }
    }));

    return {
      legend: {
        orient: 'horizontal',
        type: 'scroll',
        top: '80%',
        bottom: 3,
        left: 'center',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: getThemeColor('gray-600'),
          fontSize: 12
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['65%', '85%'],
          center: ['50%', '70%'],
          startAngle: 180,
          endAngle: 0,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#FFFFFF',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'center',
            formatter: `{b|총 요청량}\n{c|${chartData.reduce(
              (acc, item) => acc + item.requestCnt,
              0
            ).toLocaleString()}건}`,
            offset: [0, -5],
            rich: {
              b: {
                fontSize: 14,
                lineHeight: 20,
                color: '#64748B',
                fontWeight: '600'
              },
              c: {
                fontSize: 24,
                lineHeight: 30,
                fontWeight: '800',
                color: '#1E293B'
              }
            }
          },
          labelLine: { show: false },
          data: formattedData

        }
      ],
      tooltip: {
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: getThemeColor('gray-100'),
        borderColor: getThemeColor('gray-300'),
        textStyle: { color: getThemeColor('gray-1100') },
        borderWidth: 1,
        transitionDuration: 0,
        axisPointer: { type: 'none' },
        formatter: (params) => {
          const { name, value, percent } = params;
          return `
          <div style="display: flex; align-items: center;">
            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${params.color}; margin-right: 5px;"></span>
            <div style="font-weight: bold; font-size: 14px; margin-right: 5px">${name}</div>
            <div>
              <div style="font-size: 12px;">
                <span style="display: inline-block; font-weight: bold; margin-right: 3px;">${value.toLocaleString()}</span>
                <span style="font-size: 12px; color: gray;">${percent.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        `;
        }
      }
    };
  };

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: 'none',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>전체 알고리즘별 요청률</h5>
        <span

          style={{
            marginLeft: '1px',
            marginTop: 0,
            marginBottom: 0,
            padding: '2px 8px',
            // backgroundColor: '#A1C4E3', // 배경색 파란색
            backgroundColor: '#2C7BE5',
            color: 'white', // 텍스트 색상 흰색
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          {dashboardDate}

        </span>
        </div>
      </Card.Header>

      <Card.Body as={Flex} className="pt-0" alignItems="center" justifyContent="space-between">
        {isLoading && (
          <div style={{ width: '100%', height: '15rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div style={{ width: '100%', display: isLoading ? 'none' : 'block' }}>
          <ReactEchart
            echarts={echarts}
            option={getOption()}
            ref={encryptionChartRef}
            style={{ width: '100%', height: '20rem' }}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

AlgorithmCompare.propTypes = {
  totalRequests: PropTypes.number,
  encryptionValue: PropTypes.number,
  decryptionValue: PropTypes.number,
  encryptionSuccessRatio: PropTypes.number,
  decryptionSuccessRatio: PropTypes.number
};

export default AlgorithmCompare;

